import { useCallback, useState } from "react";
import { useRouter } from "next/router";
import { useRolePermissionContext } from "@Front/Stores/RolePermissionsStore";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { redirectToIdNot } from "./stepEmailIdNotPreConnect";
import { resolveStepEmailRoleFlags } from "./stepEmailRoleResolution";
import { useStepEmailRouterSync } from "./useStepEmailRouterSync";

export type StepEmailController = {
	prefilledEmail: string;
	isErrorModalOpen: number;
	isRouterReady: boolean;
	openErrorModal: (index: number) => void;
	redirectUserOnConnection: () => Promise<void>;
};

export default function useStepEmailController(): StepEmailController {
	const router = useRouter();
	const permissionContext = useRolePermissionContext();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(0);
	const [prefilledEmail, setPrefilledEmail] = useState<string>("");

	const rawError = router.query["error"];
	const errorParam = typeof rawError === "string" ? rawError : undefined;
	const rawEmailQuery = router.query["email"];
	const emailQuery = typeof rawEmailQuery === "string" ? rawEmailQuery : undefined;

	const openErrorModal = useCallback((index: number) => {
		setIsErrorModalOpen(index);
	}, []);

	const resolveRoleFlags = useCallback(() => {
		const lastKnownRole =
			typeof window !== "undefined" ? sessionStorage.getItem("last_known_role")?.toLowerCase() ?? "" : "";
		return resolveStepEmailRoleFlags({
			userType: permissionContext?.userType,
			globalRole: permissionContext?.globalRole,
			lastKnownRoleFromSession: lastKnownRole,
			customerConnected: CustomerStore.instance.isConnected(),
		});
	}, [permissionContext?.globalRole, permissionContext?.userType]);

	const redirectUserOnConnection = useCallback(async () => {
		await redirectToIdNot(resolveRoleFlags, openErrorModal);
	}, [openErrorModal, resolveRoleFlags]);

	useStepEmailRouterSync({
		router,
		emailQuery,
		errorParam,
		openErrorModal,
		setPrefilledEmail,
	});

	return {
		prefilledEmail,
		isErrorModalOpen,
		isRouterReady: router.isReady,
		openErrorModal,
		redirectUserOnConnection,
	};
}
